import React, { useEffect, useMemo, useState } from "react";
import { graphql, useStaticQuery, Link } from "gatsby";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { safeFetch, createSectionErrorLogger } from "../utils/apiUtils";
import { getYardiPropId } from "../utils/propertyMapUtil";
import { trackFeaturedUnitMoreInfoClick } from "../utils/ga4Analytics";
import {
  boxWrap,
  banner,
  contentPad,
  gridWrap,
  card,
  cardBody,
  titleRow,
  communityTitle,
  unitBadge,
  specialText,
  details,
  imageBelow,
  ctaRow,
  singleItem,
  twoItems,
} from "./featuredUnitsHome.module.scss";

const FeaturedUnitsHome = ({ citySlugFilter = null }) => {
  const data = useStaticQuery(graphql`
    query FeaturedUnitsHomeQuery {
      allContentfulFeaturedUnitsHome {
        nodes {
          unitId
          priority
          specialTextOverride
          community {
            slug
            area
            citySlug
            communityName
            rmpropid
            contactUsBanner { gatsbyImageData }
            hero { gatsbyImageData }
          }
        }
      }
      allContentfulFloorplans {
        nodes {
          communitySlug
          floorplanName
          floorplanTitle
          floorplanSlug
          beds
          numBaths
          baths
          sqFt
          bannerImage { gatsbyImageData }
        }
      }
    }
  `);

  const items = data?.allContentfulFeaturedUnitsHome?.nodes || [];
  // Apply client-side sort by priority (desc) as Contentful sort input may not include this field
  // Sort all items by priority but don't limit to 3 yet - we'll filter available ones later
  const itemsSorted = useMemo(() => {
    const sorted = [...items].sort((a, b) => (b?.priority || 0) - (a?.priority || 0));
    if (!citySlugFilter) return sorted;
    // filter by city slug; community link gives us citySlug
    const filtered = sorted.filter((n) => n.community?.citySlug?.toLowerCase() === citySlugFilter?.toLowerCase());
    return filtered;
  }, [items, citySlugFilter]);
  const allFloorplans = data?.allContentfulFloorplans?.nodes || [];

  // State for Yardi data (units and deals)
  const [unitsByCid, setUnitsByCid] = useState({});
  const [deals, setDeals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const uniqueCids = useMemo(() => {
    const set = new Set();
    itemsSorted.forEach((it) => {
      const cid = getYardiPropId(it.community?.rmpropid);
      if (cid) set.add(cid);
    });
    return Array.from(set);
  }, [itemsSorted]);

  useEffect(() => {
    let cancelled = false;
    const logError = createSectionErrorLogger("featured_units_home", {});

    const additionalFetchesFor = (cid) => {
      const urls = [];
      if (cid === "beachfl1") {
        urls.push("beachfl2", "beachfl3");
      }
      if (cid === "sc1") {
        urls.push("sc2", "sc3");
      }
      if (cid === "golfla12") {
        urls.push("golfla03");
      }
      if (cid === "willfla1") {
        urls.push("willfla2");
      }
      if (cid === "mcdonald") {
        urls.push("gleno2");
      }
      return urls;
    };

    async function loadData() {
      try {
        const nextUnitsByCid = {};

        // Fetch units per cid (with community-specific extras)
        for (const cid of uniqueCids) {
          const main = await safeFetch(
            `https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=${cid}`,
            `units for ${cid}`,
            { section: "featured_units_home", cid }
          );

          const mainUnits = main && main.res ? JSON.parse(main.res) : [];

          const extras = additionalFetchesFor(cid);
          const extraResults = await Promise.all(
            extras.map((x) =>
              safeFetch(
                `https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=${x}`,
                `units for extra ${x}`,
                { section: "featured_units_home", cid, extra: x }
              )
            )
          );

          const extraUnits = extraResults
            .filter(Boolean)
            .flatMap((d) => {
              try {
                return d && d.res ? JSON.parse(d.res) : [];
              } catch (err) {
                logError(err, "parse_extra_units", { cid });
                return [];
              }
            });

          nextUnitsByCid[cid] = [...mainUnits, ...extraUnits];
        }

        // Fetch deals once
        const dealsData = await safeFetch(
          "https://redisut.mcklab.com/index.php?action=getDeals",
          "deals",
          { section: "featured_units_home" }
        );

        if (!cancelled) {
          setUnitsByCid(nextUnitsByCid);
          setDeals(dealsData && dealsData.res ? JSON.parse(dealsData.res) : []);
          setIsLoading(false);
        }
      } catch (err) {
        logError(err, "load_data");
        if (!cancelled) setIsLoading(false);
      }
    }

    if (uniqueCids.length > 0) {
      loadData();
    } else {
      setIsLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [uniqueCids]);

  // Filter for available units based on status and limit to 3
  const availableItems = useMemo(() => {
    if (isLoading) return itemsSorted.slice(0, 3); // Show first 3 while loading
    
    const availableStatuses = ["Vacant Unrented Not Ready", "Vacant Unrented Ready", "Notice Unrented"];
    const available = [];
    
    for (const item of itemsSorted) {
      if (available.length >= 3) break; // Stop once we have 3 available units
      
      const c = item.community;
      if (!c) continue;
      
      // Check if unit exists in Yardi data with correct status
      const yardiCid = getYardiPropId(c.rmpropid);
      const communityUnitsRaw = yardiCid ? unitsByCid[yardiCid] || [] : [];
      
      // Try to find the unit
      const wantedId = String(item.unitId).trim();
      let matchedUnit = communityUnitsRaw.find(u => 
        (u.unit || u.Unit || "").trim() === wantedId
      );
      
      // Check alternate format if not found
      if (!matchedUnit && wantedId.includes('-')) {
        const parts = wantedId.split('-');
        if (parts.length === 2) {
          const alt = `${parts[1]}-${parts[0]}`;
          matchedUnit = communityUnitsRaw.find(u => 
            (u.unit || u.Unit || "").trim() === alt
          );
        }
      }
      
      // Only add if unit exists and has available status
      if (matchedUnit && availableStatuses.includes(matchedUnit.status)) {
        available.push(item);
      }
    }
    
    return available;
  }, [itemsSorted, unitsByCid, isLoading]);

  // Only render if loading or there are items to show

  // Don't render anything while loading or if no items after loading
  if (isLoading || availableItems.length === 0) {
    return null;
  }

  // Determine container size class based on number of items
  const itemCount = availableItems.length;
  const containerClass = itemCount === 1 ? singleItem : itemCount === 2 ? twoItems : '';
  const boxWrapClass = `${boxWrap} ${containerClass}`;
  const gridWrapClass = `${gridWrap} ${containerClass}`;

  // Track More Info click handler
  const handleMoreInfoClick = (trackingData) => {
    // Only track on homepage
    if (typeof window !== "undefined" && window.location.pathname === "/") {
      trackFeaturedUnitMoreInfoClick(trackingData);
    }
  };

  return (
    <section className="container" aria-label="Current Move-in Specials">
      <div className={boxWrapClass}>
        <div className={banner}>Current Move-in Specials!</div>
        <div className={contentPad}>
          <div className={gridWrapClass}>
            {availableItems.map((item, idx) => {
              const c = item.community;
              if (!c) return null;
              const link = `/apartments/${c.area.toLowerCase()}/${c.citySlug.toLowerCase()}/${c.slug.toLowerCase()}/floorplans/`;
              const img = getImage(c.hero?.gatsbyImageData||c.contactUsBanner?.gatsbyImageData);
              const overrideText = item.specialTextOverride && item.specialTextOverride !== "none" ? item.specialTextOverride : null;

              // Match unit in fetched units for this community (by yardi cid)
              const yardiCid = getYardiPropId(c.rmpropid);
              const communityUnitsRaw = yardiCid ? unitsByCid[yardiCid] || [] : [];

              // Normalize units to a simpler shape akin to floorplan page
              const normalizeUnit = (u) => {
                const avnow = ["Vacant Unrented Not Ready", "Vacant Unrented Ready"]; // availability
                const avsoon = ["Notice Unrented"];
                return {
                  id: u.ID || u.id,
                  unit: (u.unit || u.Unit || "").trim(),
                  status: u.status,
                  isVacant: avnow.includes(u.status),
                  isSoon: avsoon.includes(u.status),
                  price: u.rent1,
                  amenities: u.amenities || "",
                  position: u.position || "",
                  moveOutDate: u.date1,
                  otherDate: u.date2,
                };
              };

              const communityUnits = communityUnitsRaw.map(normalizeUnit);
              const wantedId = String(item.unitId).trim();
              let matchedUnit = communityUnits.find((u) => u.unit === wantedId);
              if (!matchedUnit && wantedId.includes('-')) {
                const parts = wantedId.split('-');
                if (parts.length === 2) {
                  const alt = `${parts[1]}-${parts[0]}`;
                  matchedUnit = communityUnits.find((u) => u.unit === alt);
                }
              }

              // Derive a floorplan name like the floorplan page (last token after " - ")
              const floorplanName = matchedUnit?.amenities
                ? matchedUnit.amenities.split(" - ").pop()
                : null;

              // Find deal text for this unit from deals list
              const matchedDeal = deals.find((d) => (d.unit || "").trim() === (matchedUnit?.unit || ""));
              const dealText = matchedDeal?.sdesc1 || null;

              // Derive simple bed/bath from amenities tokens if present
              // Examples often contain patterns like "1bd x 1ba - ..." or similar
              let bedBath = null;
              if (matchedUnit?.amenities) {
                const m = matchedUnit.amenities.match(/(\d+)bd\s*x\s*(\d+(?:\.\d+)?)ba/i);
                if (m) {
                  const beds = Number(m[1]);
                  const baths = Number(m[2]);
                  bedBath = `${beds === 0 ? 'Studio' : beds + ' bedroom'}, ${baths} bathroom`;
                }
              }

              // Try to match Contentful floorplan for image and canonical beds/baths + deep link
              const normalizeName = (str) => {
                return (str || "")
                  .toLowerCase()
                  .replace(/^the\s+/, "")
                  .replace(/\s+signature/g, "+")
                  .replace(/\s+plus/g, "+")
                  .replace(/\s+/g, " ")
                  .trim();
              };

              const cfps = (data?.allContentfulFloorplans?.nodes || []).filter((fp) => fp.communitySlug === c.slug);
              const target = normalizeName(floorplanName || "");

              let matchedFloorplan = null;
              if (target) {
                matchedFloorplan =
                  cfps.find((fp) => normalizeName(fp.floorplanName) === target) ||
                  cfps.find((fp) => normalizeName(fp.floorplanTitle || "") === target) ||
                  cfps.find((fp) => normalizeName(fp.floorplanName).includes(target)) ||
                  cfps.find((fp) => target.includes(normalizeName(fp.floorplanName)));
              }

              const fpImg = matchedFloorplan?.bannerImage?.gatsbyImageData
                ? getImage(matchedFloorplan.bannerImage.gatsbyImageData)
                : null;
              const displayBeds = matchedFloorplan?.beds ?? null;
              const displayBaths = matchedFloorplan?.baths || matchedFloorplan?.numBaths || null;
              const displaySqFt = matchedFloorplan?.sqFt ?? null;
              
              let detailsText = null;
              if (displayBeds != null && displayBaths != null) {
                const bedText = Number(displayBeds) === 0 ? 'Studio' : `${displayBeds} Bed`;
                const bathText = `${displayBaths} Bath`;
                const sqftText = displaySqFt ? ` ${displaySqFt} SqFt` : '';
                detailsText = `${bedText} ${bathText}${sqftText}`;
              } else {
                detailsText = bedBath || null;
              }
              const floorplanLink = matchedFloorplan?.floorplanSlug
                ? `/apartments/${c.area.toLowerCase()}/${c.citySlug.toLowerCase()}/${c.slug.toLowerCase()}/floorplans/${matchedFloorplan.floorplanSlug}/`
                : link;

              // Prepare tracking data for More Info click
              const trackingData = {
                community_id: c.slug,
                community_name: c.communityName,
                unit_id: item.unitId,
                floorplan_name: floorplanName || matchedFloorplan?.floorplanName || "",
                price: matchedUnit?.price ? Number(matchedUnit.price) : 0,
                beds: displayBeds !== null ? Number(displayBeds) : 0,
                baths: displayBaths !== null ? Number(displayBaths) : 0,
                sqft: displaySqFt !== null ? Number(displaySqFt) : 0,
                index: idx,
                total_cards: availableItems.length,
              };

              return (
                <article className={card} key={`${c.slug}-${item.unitId}-${idx}`}>
                  <div className={cardBody}>
                    {/* Special banner (green with white text) */}
                    {(overrideText || dealText) && (
                      <div className="floorplan-deal-text-card" style={{ backgroundColor: '#a3b49d', color: '#fff', borderRadius: 5, padding: '6px 10px', textAlign: 'center', fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.75rem' }}>
                        {overrideText ? overrideText : dealText}
                      </div>
                    )}
                    {/* Community name styled like floorplan title */}
                    <Link to={floorplanLink} className="title is-4" style={{ display: 'block', color: '#A3B49D', margin: '0rem 0', fontSize: '1.2rem' }}>
                      {c.communityName}
                    </Link>
                    {detailsText && <p className={details}>{detailsText}</p>}
                      {matchedUnit && matchedUnit.price && (
                        <p style={{ fontSize: '1.2rem', fontWeight: '600', color: '#333', marginBottom: '0.5rem' }}>
                          ${Number(matchedUnit.price)}
                        </p>
                      )}
                    {/* Floorplan image below details */}
                    {(fpImg || img) && (
                      <div className={imageBelow} style={{ height: '160px', overflow: 'hidden', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', marginTop: 'auto' }}>
                        <GatsbyImage 
                          image={fpImg || img} 
                          alt={`${c.communityName} floorplan image`}
                          style={{ maxHeight: '160px' }}
                          imgStyle={{ objectFit: 'contain', maxHeight: '160px' }}
                          key={`${c.slug}-${item.unitId}-${idx}`}
                          placeholder="blurred"
                        />
                      </div>
                    )}
                    <div className={ctaRow}>
                      <Link 
                        to={floorplanLink} 
                        className="button is-link" 
                        style={{ width: '80%' }} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        onClick={() => handleMoreInfoClick(trackingData)}
                      >
                        More Info
                      </Link>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedUnitsHome;


